import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Table, Badge } from "reactstrap";
import { getComparativaProveedores } from "../../helpers/backend_helper";
import ModalOutliersProveedor from "./ModalOutliersProveedor";
import ModalOutliersDesviacionProveedor from "./ModalOutliersDesviacionProveedor";
import WidgetLoader from "./WidgetLoader";

// Estilos para animaciones
const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .fade-in-animation {
    animation: fadeIn 0.3s ease-in-out;
  }
`;

const ComparativaProveedores = ({ fechaInicio, fechaFin, proveedorSeleccionado, aseguradora }) => {
  const [comparativa, setComparativa] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [modalOutliers, setModalOutliers] = useState(false);
  const [modalOutliersDesviacion, setModalOutliersDesviacion] = useState(false);
  const [proveedorSeleccionadoModal, setProveedorSeleccionadoModal] = useState(null);

  const abrirModalOutliers = (proveedor) => {
    setProveedorSeleccionadoModal(proveedor);
    setModalOutliers(true);
  };

  const cerrarModalOutliers = () => {
    setModalOutliers(false);
    setProveedorSeleccionadoModal(null);
  };

  const abrirModalOutliersDesviacion = (proveedor) => {
    setProveedorSeleccionadoModal(proveedor);
    setModalOutliersDesviacion(true);
  };

  const cerrarModalOutliersDesviacion = () => {
    setModalOutliersDesviacion(false);
    setProveedorSeleccionadoModal(null);
  };

  useEffect(() => {
    const cargarComparativa = async () => {
      if (!fechaInicio || !fechaFin) return;

      setCargando(true);
      setError(null);

      try {
        const params = {
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin
        };

        if (proveedorSeleccionado && proveedorSeleccionado.trim() !== "") {
          params.codigo_proveedor = proveedorSeleccionado;
        }

        if (aseguradora) {
          params.aseguradora = aseguradora;
        }

        const response = await getComparativaProveedores(params);
        const data = response.data;

        // Manejar formato con outliers de facturas
        if (data.data) {
          if (data.data.proveedores && Array.isArray(data.data.proveedores)) {
            setComparativa(data.data.proveedores);
          } else if (Array.isArray(data.data)) {
            // Formato antiguo (retrocompatibilidad)
            setComparativa(data.data);
          } else {
            setComparativa([]);
          }
        } else {
          setComparativa([]);
        }
      } catch (err) {
        console.error("Error al cargar comparativa:", err);
        setError(err.message || "Error al cargar comparativa de proveedores");
      } finally {
        setCargando(false);
      }
    };

    cargarComparativa();
  }, [fechaInicio, fechaFin, proveedorSeleccionado, aseguradora]);

  const formatearNumero = (numero, decimales = 2) => {
    return Number(numero).toLocaleString("es-VE", {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  };

  if (cargando) {
    return (
      <Card>
        <CardHeader>
          <h5 className="card-title mb-0">Comparativa por Proveedor</h5>
        </CardHeader>
        <CardBody>
          <WidgetLoader message="Cargando comparativa de proveedores..." />
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <h5 className="card-title mb-0">Comparativa por Proveedor</h5>
        </CardHeader>
        <CardBody>
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </CardBody>
      </Card>
    );
  }

  if (comparativa.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h5 className="card-title mb-0">Comparativa por Proveedor</h5>
        </CardHeader>
        <CardBody>
          <div className="text-center py-4">
            <i className="ri-file-list-3-line display-4 text-muted"></i>
            <p className="text-muted mt-2">No hay datos disponibles para el período seleccionado</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
    {/* Inyectar estilos CSS */}
    <style>{styles}</style>
    
    <Card className="fade-in-animation">
      <CardHeader>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5 className="card-title mb-0">
              <i className="ri-bar-chart-grouped-line me-2"></i>
              Comparativa por Proveedor
            </h5>
            <p className="text-muted mb-0 mt-1">
              Resumen de métricas por cada proveedor en el período seleccionado
            </p>
          </div>
          {comparativa.filter(p => p.tiene_outliers).length > 0 && (
            <div className="text-end">
              <div className="alert alert-danger py-2 px-3 mb-0" role="alert">
                <i className="ri-alert-line me-1"></i>
                <small className="fw-medium">
                  {comparativa.filter(p => p.tiene_outliers).length} proveedor(es) con facturas extremas
                </small>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {/* Leyenda explicativa - Responsive */}
        <div className="mb-3 p-2 bg-light rounded">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <small className="text-muted fw-medium d-none d-md-inline">Métodos:</small>
            <div className="d-flex align-items-center gap-2">
              <Badge color="danger" className="fs-11">
                <i className="ri-alert-line me-1"></i>IQR
              </Badge>
              <small className="text-muted d-none d-lg-inline">Rango Intercuartílico</small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge color="primary" className="fs-11">
                <i className="ri-line-chart-line me-1"></i>σ
              </Badge>
              <small className="text-muted d-none d-lg-inline">Desviación Estándar</small>
            </div>
            <small className="text-muted ms-auto d-none d-md-inline">
              <i className="ri-cursor-line me-1"></i>Click en valores para detalles
            </small>
          </div>
        </div>

        {/* Indicador de scroll */}
        <div className="text-center mb-2">
          <small className="text-muted">
            <i className="ri-arrow-left-right-line me-1"></i>
            Desliza horizontalmente para ver todas las columnas
          </small>
        </div>

        <div className="table-responsive">
          <Table className="table-hover table-striped align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th rowSpan="2" className="align-middle">Proveedor</th>
                <th rowSpan="2" className="text-center align-middle" style={{minWidth: '80px'}}>Facturas</th>
                <th rowSpan="2" className="text-end align-middle" style={{minWidth: '100px'}}>Total</th>
                <th rowSpan="2" className="text-end align-middle" style={{minWidth: '90px'}}>Promedio</th>
                <th colSpan="4" className="text-center bg-danger text-white">Facturas Agudas</th>
                <th colSpan="4" className="text-center bg-primary text-white">Facturas Crónicas</th>
                <th rowSpan="2" className="text-center align-middle">Delivery</th>
                <th rowSpan="2" className="text-center align-middle" title="Promedio de todas las calificaciones (incluye múltiples calificaciones por solicitud crónica)">
                  Calificación
                  <i className="ri-information-line ms-1 text-muted" style={{fontSize: '0.8rem'}}></i>
                </th>
              </tr>
              <tr>
                <th className="text-center bg-danger-subtle text-danger" title="Total de facturas agudas" style={{minWidth: '70px'}}>
                  <small className="fw-medium">Total</small>
                </th>
                <th className="text-end bg-danger-subtle text-danger" title="Promedio de facturas agudas" style={{minWidth: '90px'}}>
                  <small className="fw-medium">Promedio</small>
                </th>
                <th className="text-center bg-danger-subtle text-danger" title="Outliers detectados por método IQR" style={{minWidth: '70px'}}>
                  <small className="fw-medium"><i className="ri-alert-line"></i> IQR</small>
                </th>
                <th className="text-center bg-danger-subtle text-danger" title="Outliers detectados por desviación estándar" style={{minWidth: '70px'}}>
                  <small className="fw-medium"><i className="ri-line-chart-line"></i> σ</small>
                </th>
                <th className="text-center bg-primary-subtle text-primary" title="Total de facturas crónicas" style={{minWidth: '70px'}}>
                  <small className="fw-medium">Total</small>
                </th>
                <th className="text-end bg-primary-subtle text-primary" title="Promedio de facturas crónicas" style={{minWidth: '90px'}}>
                  <small className="fw-medium">Promedio</small>
                </th>
                <th className="text-center bg-primary-subtle text-primary" title="Outliers detectados por método IQR" style={{minWidth: '70px'}}>
                  <small className="fw-medium"><i className="ri-alert-line"></i> IQR</small>
                </th>
                <th className="text-center bg-primary-subtle text-primary" title="Outliers detectados por desviación estándar" style={{minWidth: '70px'}}>
                  <small className="fw-medium"><i className="ri-line-chart-line"></i> σ</small>
                </th>
              </tr>
            </thead>
            <tbody>
              {comparativa.map((prov, index) => {
                const esSeleccionado = proveedorSeleccionado === prov.codigo;
                const esGrupo = prov.es_grupo === true;
                const esHijo = prov.es_hijo === true;
                
                return (
                  <tr
                    key={prov.codigo}
                    className={`${esSeleccionado ? "table-primary" : ""} ${esGrupo ? "fw-bold bg-light" : ""}`}
                  >
                    <td>
                      <div className="d-flex align-items-center">
                        {/* Indentación para hijos */}
                        {esHijo && (
                          <div className="me-2" style={{ width: '20px' }}>
                            <i className="ri-corner-down-right-line text-muted"></i>
                          </div>
                        )}
                        
                        {/* Avatar con número */}
                        {!esHijo && (
                          <div className="flex-shrink-0 me-2">
                            <div
                              className={`avatar-xs rounded-circle ${
                                index === 0
                                  ? "bg-success-subtle"
                                  : index === 1
                                  ? "bg-info-subtle"
                                  : index === 2
                                  ? "bg-warning-subtle"
                                  : "bg-secondary-subtle"
                              }`}
                            >
                              <span
                                className={`avatar-title rounded-circle fs-14 ${
                                  index === 0
                                    ? "text-success"
                                    : index === 1
                                    ? "text-info"
                                    : index === 2
                                    ? "text-warning"
                                    : "text-secondary"
                                }`}
                              >
                                {esGrupo ? <i className="ri-folder-line"></i> : index + 1}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex-grow-1">
                          <h6 className={`mb-0 ${esGrupo ? 'fs-15' : 'fs-14'}`}>
                            {prov.nombre}
                            {esGrupo && (
                              <span className="badge bg-primary-subtle text-primary ms-2 fs-11">
                                <i className="ri-folder-line me-1"></i>Grupo
                              </span>
                            )}
                          </h6>
                          <small className="text-muted">{prov.codigo}</small>
                        </div>
                        {esSeleccionado && (
                          <span className="badge bg-primary ms-2">
                            <i className="ri-eye-line"></i> Viendo
                          </span>
                        )}
                        {prov.tiene_outliers && (
                          <span className="badge bg-danger-subtle text-danger ms-2" title="Tiene valores extremos">
                            <i className="ri-alert-line"></i>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-info-subtle text-info fs-12">
                        {prov.total_facturas}
                      </span>
                    </td>
                    <td className="text-end">
                      <span className="text-success fw-medium">
                        $ {formatearNumero(prov.total_usd)}
                      </span>
                    </td>
                    <td className="text-end">
                      <small className="text-muted">
                        $ {formatearNumero(prov.promedio_usd)}
                      </small>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-danger-subtle text-danger">
                        {prov.facturas_agudas}
                      </span>
                    </td>
                    <td className="text-end">
                      <small className="text-danger fw-medium">
                        $ {formatearNumero(prov.promedio_agudas_usd || 0)}
                      </small>
                    </td>
                    <td className="text-center">
                      {prov.outliers_facturas?.agudas?.cantidad_outliers > 0 ? (
                        <div 
                          onClick={() => abrirModalOutliers(prov)}
                          className="p-1 rounded"
                          style={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          title="Click para ver detalles de outliers IQR"
                        >
                          <span className="badge bg-danger fs-11">
                            <i className="ri-alert-fill"></i> {prov.outliers_facturas.agudas.cantidad_outliers}
                          </span>
                          <div className="mt-1">
                            <small className="text-danger fw-medium" style={{fontSize: '10px'}}>
                              {prov.outliers_facturas.agudas.porcentaje_outliers}%
                            </small>
                          </div>
                        </div>
                      ) : (
                        <span className="text-success fs-12">
                          <i className="ri-checkbox-circle-line"></i>
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      {prov.outliers_desviacion?.agudas?.cantidad_outliers > 0 ? (
                        <div 
                          onClick={() => abrirModalOutliersDesviacion(prov)}
                          className="p-1 rounded"
                          style={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          title="Click para ver detalles de desviación estándar"
                        >
                          <span className="badge bg-danger-subtle text-danger fs-11">
                            <i className="ri-line-chart-line"></i> {prov.outliers_desviacion.agudas.cantidad_outliers}
                          </span>
                          <div className="mt-1">
                            <small className="text-danger fw-medium" style={{fontSize: '10px'}}>
                              {prov.outliers_desviacion.agudas.porcentaje_outliers}%
                            </small>
                          </div>
                        </div>
                      ) : (
                        <span className="text-success fs-12">
                          <i className="ri-checkbox-circle-line"></i>
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      <span className="badge bg-primary-subtle text-primary">
                        {prov.facturas_cronicas}
                      </span>
                    </td>
                    <td className="text-end">
                      <small className="text-primary fw-medium">
                        $ {formatearNumero(prov.promedio_cronicas_usd || 0)}
                      </small>
                    </td>
                    <td className="text-center">
                      {prov.outliers_facturas?.cronicas?.cantidad_outliers > 0 ? (
                        <div 
                          onClick={() => abrirModalOutliers(prov)}
                          className="p-1 rounded"
                          style={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          title="Click para ver detalles de outliers IQR"
                        >
                          <span className="badge bg-primary fs-11">
                            <i className="ri-alert-fill"></i> {prov.outliers_facturas.cronicas.cantidad_outliers}
                          </span>
                          <div className="mt-1">
                            <small className="text-primary fw-medium" style={{fontSize: '10px'}}>
                              {prov.outliers_facturas.cronicas.porcentaje_outliers}%
                            </small>
                          </div>
                        </div>
                      ) : (
                        <span className="text-success fs-12">
                          <i className="ri-checkbox-circle-line"></i>
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      {prov.outliers_desviacion?.cronicas?.cantidad_outliers > 0 ? (
                        <div 
                          onClick={() => abrirModalOutliersDesviacion(prov)}
                          className="p-1 rounded"
                          style={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          title="Click para ver detalles de desviación estándar"
                        >
                          <span className="badge bg-primary-subtle text-primary fs-11">
                            <i className="ri-line-chart-line"></i> {prov.outliers_desviacion.cronicas.cantidad_outliers}
                          </span>
                          <div className="mt-1">
                            <small className="text-primary fw-medium" style={{fontSize: '10px'}}>
                              {prov.outliers_desviacion.cronicas.porcentaje_outliers}%
                            </small>
                          </div>
                        </div>
                      ) : (
                        <span className="text-success fs-12">
                          <i className="ri-checkbox-circle-line"></i>
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      {prov.delivery > 0 ? (
                        <span className="text-success">
                          <i className="ri-checkbox-circle-fill"></i> {prov.delivery}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-center">
                      {prov.calificacion > 0 ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <i className="ri-star-fill text-warning me-1"></i>
                          <span className="fw-medium">{formatearNumero(prov.calificacion, 1)}</span>
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {(() => {
              // Excluir filas de grupo para evitar doble conteo (el grupo es resumen de sus hijos)
              const provSinGrupos = comparativa.filter(p => !p.es_grupo);
              const totalFacturas = provSinGrupos.reduce((sum, p) => sum + p.total_facturas, 0);
              const totalUsd = provSinGrupos.reduce((sum, p) => sum + p.total_usd, 0);
              const totalAgudas = provSinGrupos.reduce((sum, p) => sum + p.facturas_agudas, 0);
              const totalCronicas = provSinGrupos.reduce((sum, p) => sum + p.facturas_cronicas, 0);
              const sumPromedioAgudas = provSinGrupos.reduce((sum, p) => sum + (p.promedio_agudas_usd || 0) * p.facturas_agudas, 0);
              const sumPromedioCronicas = provSinGrupos.reduce((sum, p) => sum + (p.promedio_cronicas_usd || 0) * p.facturas_cronicas, 0);
              const totalOutliersIqrAgudas = provSinGrupos.reduce((sum, p) => sum + (p.outliers_facturas?.agudas?.cantidad_outliers || 0), 0);
              const totalOutliersSigmaAgudas = provSinGrupos.reduce((sum, p) => sum + (p.outliers_desviacion?.agudas?.cantidad_outliers || 0), 0);
              const totalOutliersIqrCronicas = provSinGrupos.reduce((sum, p) => sum + (p.outliers_facturas?.cronicas?.cantidad_outliers || 0), 0);
              const totalOutliersSigmaCronicas = provSinGrupos.reduce((sum, p) => sum + (p.outliers_desviacion?.cronicas?.cantidad_outliers || 0), 0);

              return (
              <tfoot className="table-light border-top border-2">
                <tr className="fw-bold">
                  <td className="bg-light">
                    <i className="ri-calculator-line me-1"></i>
                    TOTALES
                  </td>
                  <td className="text-center bg-light">
                    <Badge 
                      color="info" 
                      className="fs-12"
                      title={`Total de facturas de todos los proveedores`}
                    >
                      {totalFacturas}
                    </Badge>
                  </td>
                  <td className="text-end bg-light">
                    <span className="text-success">
                      $ {formatearNumero(totalUsd)}
                    </span>
                  </td>
                  <td className="bg-light"></td>
                  <td className="text-center bg-danger-subtle">
                    {totalAgudas}
                  </td>
                  <td className="text-end bg-danger-subtle">
                    <small className="text-danger fw-medium">
                      $ {formatearNumero(totalAgudas > 0 ? sumPromedioAgudas / totalAgudas : 0)}
                    </small>
                  </td>
                  <td className="text-center bg-danger-subtle">
                    <Badge 
                      color="danger" 
                      className="fs-11"
                      title={`${totalAgudas > 0 ? ((totalOutliersIqrAgudas / totalAgudas) * 100).toFixed(1) : 0}% del total de facturas agudas`}
                    >
                      {totalOutliersIqrAgudas}
                    </Badge>
                  </td>
                  <td className="text-center bg-danger-subtle">
                    <Badge 
                      color="danger" 
                      className="fs-11"
                      title={`${totalAgudas > 0 ? ((totalOutliersSigmaAgudas / totalAgudas) * 100).toFixed(1) : 0}% del total de facturas agudas`}
                    >
                      {totalOutliersSigmaAgudas}
                    </Badge>
                  </td>
                  <td className="text-center bg-primary-subtle">
                    {totalCronicas}
                  </td>
                  <td className="text-end bg-primary-subtle">
                    <small className="text-primary fw-medium">
                      $ {formatearNumero(totalCronicas > 0 ? sumPromedioCronicas / totalCronicas : 0)}
                    </small>
                  </td>
                  <td className="text-center bg-primary-subtle">
                    <Badge 
                      color="primary" 
                      className="fs-11"
                      title={`${totalCronicas > 0 ? ((totalOutliersIqrCronicas / totalCronicas) * 100).toFixed(1) : 0}% del total de facturas crónicas`}
                    >
                      {totalOutliersIqrCronicas}
                    </Badge>
                  </td>
                  <td className="text-center bg-primary-subtle">
                    <Badge 
                      color="primary" 
                      className="fs-11"
                      title={`${totalCronicas > 0 ? ((totalOutliersSigmaCronicas / totalCronicas) * 100).toFixed(1) : 0}% del total de facturas crónicas`}
                    >
                      {totalOutliersSigmaCronicas}
                    </Badge>
                  </td>
                  <td colSpan="2" className="text-muted text-center bg-light">
                    <small><i className="ri-information-line me-1"></i>Suma global de todos los proveedores</small>
                  </td>
                </tr>
              </tfoot>
              );
            })()}
          </Table>
        </div>
      </CardBody>
    </Card>

    {/* Modal de Outliers (IQR) */}
    <ModalOutliersProveedor
      isOpen={modalOutliers}
      toggle={cerrarModalOutliers}
      proveedor={proveedorSeleccionadoModal}
      fechaInicio={fechaInicio}
      fechaFin={fechaFin}
      aseguradora={aseguradora}
    />

    {/* Modal de Outliers (Desviación Estándar) */}
    <ModalOutliersDesviacionProveedor
      isOpen={modalOutliersDesviacion}
      toggle={cerrarModalOutliersDesviacion}
      proveedor={proveedorSeleccionadoModal}
      fechaInicio={fechaInicio}
      fechaFin={fechaFin}
      aseguradora={aseguradora}
    />
    </>
  );
};

export default ComparativaProveedores;
